import { DateTime } from 'luxon';
import { SCHEDULE_TYPE } from '@/core/constants/daysOfWeek';
import { isValidDayOfWeek, getDayIndex } from '@/core/utils/daysOfWeek';
import { OperationModeSchedule, OperationModesScheduleGroup } from '@features/shopautomation/types.ts';

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const getScheduleDates = (activeOn: string | null | undefined): string[] =>
    (activeOn ?? '')
        .split(',')
        .map((d) => d.trim())
        .filter((d) => DATE_REGEX.test(d))
        .sort();

export const isValidScheduleDateTime = (value: string): boolean => DateTime.fromISO(value).isValid;

export const getSortedOperationModesSchedules = (
    operationModesSchedules: OperationModesScheduleGroup[]
): [OperationModesScheduleGroup[], OperationModesScheduleGroup[]] => {
    const getScheduleDayIndex = (activeOn: string): number => {
        const givenDay = activeOn.split(',')[0];
        return isValidDayOfWeek(givenDay) ? getDayIndex(givenDay) : -1;
    };

    const today = DateTime.local().toISODate()!;
    const days: OperationModesScheduleGroup[] = [];
    const upcoming: { item: OperationModesScheduleGroup; sortKey: string }[] = [];
    const expired: { item: OperationModesScheduleGroup; sortKey: string }[] = [];

    for (const item of operationModesSchedules) {
        const firstElementOfActiveOn = item.activeOn?.split(',')[0]?.trim();
        if (!firstElementOfActiveOn) continue;

        if (isValidDayOfWeek(firstElementOfActiveOn)) {
            days.push(item);
            continue;
        }

        const dates = getScheduleDates(item.activeOn);
        if (!dates.length) continue;

        const lastDate = dates[dates.length - 1];
        if (lastDate >= today) {
            upcoming.push({ item, sortKey: lastDate });
        } else {
            expired.push({ item, sortKey: lastDate });
        }
    }

    days.sort((a, b) => getScheduleDayIndex(a.activeOn || '') - getScheduleDayIndex(b.activeOn || ''));
    const standardSchedule = operationModesSchedules.find((item) => item.activeOn === SCHEDULE_TYPE.STANDARD);
    if (standardSchedule) {
        days.unshift(standardSchedule);
    }

    upcoming.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    expired.sort((a, b) => a.sortKey.localeCompare(b.sortKey));

    return [[...days, ...upcoming.map((x) => x.item)], expired.map((x) => x.item)];
};

export const getActiveOnTranslation = (
    operationModesSchedule: OperationModesScheduleGroup,
    t: (key: string) => string
): string => {
    if (!operationModesSchedule.activeOn) return '';
    const cardTitle = operationModesSchedule.activeOn
        .split(',')
        .map((x) => (isValidScheduleDateTime(x) ? x : t(x)))
        .join(', ');
    if (!operationModesSchedule.name || operationModesSchedule.name === '') {
        return cardTitle;
    }
    return `${cardTitle} - ${operationModesSchedule.name}`;
};

export const getSortedOperationModeSchedules = (schedules: OperationModeSchedule[]): OperationModeSchedule[] => {
    return [...schedules].sort((a, b) => {
        const timeA = a.from.split(':').map(Number);
        const timeB = b.from.split(':').map(Number);
        return timeA[0] * 60 + timeA[1] - (timeB[0] * 60 + timeB[1]);
    });
};

export const upsertOperationModesSchedule = (
    schedules: OperationModesScheduleGroup[],
    item: OperationModesScheduleGroup
): { schedules: OperationModesScheduleGroup[]; isNew: boolean } => {
    const existingIndex = schedules.findIndex(
        (x) =>
            (item.activeOn === SCHEDULE_TYPE.STANDARD && x.activeOn === SCHEDULE_TYPE.STANDARD) ||
            (item.id && item.id === x.id) ||
            (item.placeholderId && item.placeholderId === x.placeholderId)
    );

    const updatedSchedules =
        existingIndex < 0
            ? [...schedules, item]
            : [...schedules.slice(0, existingIndex), item, ...schedules.slice(existingIndex + 1)];

    return { schedules: updatedSchedules, isNew: existingIndex < 0 };
};

export const removeOperationModesSchedule = (
    schedules: OperationModesScheduleGroup[],
    target: OperationModesScheduleGroup
): OperationModesScheduleGroup[] => {
    const index = schedules.findIndex(
        (x) =>
            (x.id && target.id && x.id === target.id) ||
            (x.placeholderId && target.placeholderId && x.placeholderId === target.placeholderId)
    );

    if (index === -1) return schedules;

    return [...schedules.slice(0, index), ...schedules.slice(index + 1)];
};
